/**
 * Convert image to grayscale and extract outlines (edge detection) for coloring book effect.
 * Uses grayscale + gaussian blur + Sobel magnitude + adaptive threshold (Otsu) + light cleanup.
 */

const SOBEL_X = [
  [-1, 0, 1],
  [-2, 0, 2],
  [-1, 0, 1],
];

const SOBEL_Y = [
  [-1, -2, -1],
  [0, 0, 0],
  [1, 2, 1],
];

function getPixel(data, w, h, x, y) {
  if (x < 0 || x >= w || y < 0 || y >= h) return 128;
  const i = (y * w + x) * 4;
  return (data[i] + data[i + 1] + data[i + 2]) / 3;
}

function convolve(data, w, h, kernel) {
  const out = new Uint8ClampedArray(w * h);
  const kSize = 3;
  const half = Math.floor(kSize / 2);

  for (let y = half; y < h - half; y++) {
    for (let x = half; x < w - half; x++) {
      let sum = 0;
      for (let ky = 0; ky < kSize; ky++) {
        for (let kx = 0; kx < kSize; kx++) {
          const px = getPixel(data, w, h, x + kx - half, y + ky - half);
          sum += px * kernel[ky][kx];
        }
      }
      out[y * w + x] = Math.min(255, Math.max(0, Math.abs(sum)));
    }
  }
  return out;
}

function convolveSigned(data, w, h, kernel) {
  const out = new Float32Array(w * h)
  const kSize = 3
  const half = Math.floor(kSize / 2)
  for (let y = half; y < h - half; y++) {
    for (let x = half; x < w - half; x++) {
      let sum = 0
      for (let ky = 0; ky < kSize; ky++) {
        for (let kx = 0; kx < kSize; kx++) {
          const px = getPixel(data, w, h, x + kx - half, y + ky - half)
          sum += px * kernel[ky][kx]
        }
      }
      out[y * w + x] = sum
    }
  }
  return out
}

function gaussianBlurGray(gray, w, h) {
  // 5x5 gaussian kernel (sum=273)
  const k = [
    [1, 4, 7, 4, 1],
    [4, 16, 26, 16, 4],
    [7, 26, 41, 26, 7],
    [4, 16, 26, 16, 4],
    [1, 4, 7, 4, 1],
  ]
  const out = new Uint8ClampedArray(w * h)
  const half = 2
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0
      let wsum = 0
      for (let ky = -half; ky <= half; ky++) {
        for (let kx = -half; kx <= half; kx++) {
          const yy = Math.max(0, Math.min(h - 1, y + ky))
          const xx = Math.max(0, Math.min(w - 1, x + kx))
          const ww = k[ky + half][kx + half]
          sum += gray[yy * w + xx] * ww
          wsum += ww
        }
      }
      out[y * w + x] = sum / wsum
    }
  }
  return out
}

function dogEdges(gray, w, h, passesSmall = 1, passesLarge = 3, k = 1.55) {
  // Difference-of-Gaussians on grayscale:
  // strong at edges, suppresses flat shading/gradients => cleaner "ink" lines.
  let gSmall = gray
  let gLarge = gray
  for (let i = 0; i < Math.max(1, passesSmall); i++) gSmall = gaussianBlurGray(gSmall, w, h)
  for (let i = 0; i < Math.max(1, passesLarge); i++) gLarge = gaussianBlurGray(gLarge, w, h)
  const out = new Float32Array(w * h)
  const kk = typeof k === 'number' ? k : 1.55
  for (let i = 0; i < w * h; i++) out[i] = gSmall[i] - kk * gLarge[i]
  return out
}

function xdogBinary(gray, w, h, options = {}) {
  // XDoG-inspired binarization tuned for "coloring book" outlines.
  // Produces black lines on white background with fewer textures.
  const pSmall = Math.max(1, Math.round(options.xdogSmallPasses ?? 1))
  const pLarge = Math.max(pSmall + 1, Math.round(options.xdogLargePasses ?? 4))
  const k = typeof options.xdogK === 'number' ? options.xdogK : 1.55
  const contrast = typeof options.xdogContrast === 'number' ? options.xdogContrast : 10.0
  const tau = typeof options.xdogTau === 'number' ? options.xdogTau : 0.98
  const eps = typeof options.xdogEps === 'number' ? options.xdogEps : 0.02

  const dog = dogEdges(gray, w, h, pSmall, pLarge, k)
  const resp = new Uint8ClampedArray(w * h)
  for (let i = 0; i < w * h; i++) {
    // Map DoG response to [0..255] via tanh curve (edge enhancement).
    const v = 1 + Math.tanh(contrast * (dog[i] / 255))
    const x = v * 0.5 // ~[0..1]
    resp[i] = Math.max(0, Math.min(255, Math.round(x * 255)))
  }

  // Threshold: prefer clean lines (lower threshold = more lines).
  const base = otsuThreshold(resp)
  const t = Math.max(10, Math.min(245, Math.round(base * tau)))
  const binary = new Uint8ClampedArray(w * h)
  for (let i = 0; i < w * h; i++) {
    // Keep only strong ink candidates.
    // If we keep resp[i] < threshold we can invert/over-shoot into "filled black".
    // Here we treat higher resp as stronger ink and use eps as extra strictness.
    const strict = t + eps * 255
    binary[i] = resp[i] > strict ? 1 : 0
  }
  return binary
}

function objectContourBinary(gray, w, h, options = {}) {
  // Object-only outlines:
  // 1) Segment an "object" mask from grayscale (Otsu + invert if needed)
  // 2) Clean the mask
  // 3) Extract only the boundary contour via morphological gradient
  const total = w * h
  const closeMaskPasses = Math.max(0, Math.round(typeof options.closeMaskPasses === 'number' ? options.closeMaskPasses : 2))
  const openMaskPasses = Math.max(0, Math.round(typeof options.openMaskPasses === 'number' ? options.openMaskPasses : 1))
  const majorityMaskPasses = Math.max(0, Math.round(typeof options.majorityMaskPasses === 'number' ? options.majorityMaskPasses : 1))
  const majorityMaskThreshold = typeof options.majorityMaskThreshold === 'number' ? options.majorityMaskThreshold : 6

  const contourErodePasses = Math.max(0, Math.round(typeof options.contourErodePasses === 'number' ? options.contourErodePasses : 0))
  const contourDilatePasses = Math.max(0, Math.round(typeof options.contourDilatePasses === 'number' ? options.contourDilatePasses : 0))
  const edgeMajorityPasses = Math.max(0, Math.round(typeof options.edgeMajorityPasses === 'number' ? options.edgeMajorityPasses : 0))
  const edgeMajorityThreshold = typeof options.edgeMajorityThreshold === 'number' ? options.edgeMajorityThreshold : 6

  // Use Otsu on grayscale values (gray is already blurred).
  const thr = otsuThreshold(gray)
  const maskThresholdScale = typeof options.maskThresholdScale === 'number' ? options.maskThresholdScale : 1.03
  const tMask = Math.max(1, Math.min(254, Math.round(thr * maskThresholdScale)))

  // Assume object is darker than background. If mask is too large, invert.
  let mask = new Uint8ClampedArray(total)
  let onCount = 0
  for (let i = 0; i < total; i++) {
    const v = gray[i]
    const on = v < tMask ? 1 : 0
    mask[i] = on
    onCount += on
  }
  // If mask is nearly empty or nearly full, invert (this makes it robust
  // when the "object" is lighter than background).
  if (onCount < total * 0.05 || onCount > total * 0.95) {
    for (let i = 0; i < total; i++) mask[i] = mask[i] ? 0 : 1
  }

  if (closeMaskPasses > 0) mask = closeBinary(mask, w, h, closeMaskPasses)
  if (openMaskPasses > 0) mask = openBinary(mask, w, h, openMaskPasses)
  for (let i = 0; i < majorityMaskPasses; i++) mask = majorityFilter(mask, w, h, majorityMaskThreshold)

  // Morphological gradient = dilate - erode (boundary only)
  const di = dilate(mask, w, h)
  const er = erode(mask, w, h)
  let contour = new Uint8ClampedArray(total)
  // XOR => boundary (both sides), avoids one-sided "missing lines" cases.
  for (let i = 0; i < total; i++) contour[i] = (di[i] !== er[i]) ? 1 : 0

  for (let i = 0; i < contourErodePasses; i++) contour = erode(contour, w, h)
  for (let i = 0; i < contourDilatePasses; i++) contour = dilate(contour, w, h)

  for (let i = 0; i < edgeMajorityPasses; i++) contour = majorityFilter(contour, w, h, edgeMajorityThreshold)

  return contour
}

function objectXdogBinary(gray, w, h, options = {}) {
  // Hybrid: segment object mask -> run XDoG -> keep only XDoG edges inside the object.
  // This restores internal "ink" details while removing background noise.
  const total = w * h

  const closeMaskPasses = Math.max(0, Math.round(typeof options.closeMaskPasses === 'number' ? options.closeMaskPasses : 2))
  const openMaskPasses = Math.max(0, Math.round(typeof options.openMaskPasses === 'number' ? options.openMaskPasses : 1))
  const majorityMaskPasses = Math.max(0, Math.round(typeof options.majorityMaskPasses === 'number' ? options.majorityMaskPasses : 0))
  const majorityMaskThreshold = typeof options.majorityMaskThreshold === 'number' ? options.majorityMaskThreshold : 6
  const maskDilatePasses = Math.max(0, Math.round(typeof options.maskDilatePasses === 'number' ? options.maskDilatePasses : 1))
  const maskThresholdScale = typeof options.maskThresholdScale === 'number' ? options.maskThresholdScale : 1.03

  const thr = otsuThreshold(gray)
  const tMask = Math.max(1, Math.min(254, Math.round(thr * maskThresholdScale)))

  let mask = new Uint8ClampedArray(total)
  let onCount = 0
  for (let i = 0; i < total; i++) {
    const v = gray[i]
    const on = v < tMask ? 1 : 0
    mask[i] = on
    onCount += on
  }

  // Invert when the segmentation is clearly on the wrong side.
  if (onCount < total * 0.05 || onCount > total * 0.95) {
    for (let i = 0; i < total; i++) mask[i] = mask[i] ? 0 : 1
  }

  if (closeMaskPasses > 0) mask = closeBinary(mask, w, h, closeMaskPasses)
  if (openMaskPasses > 0) mask = openBinary(mask, w, h, openMaskPasses)
  for (let i = 0; i < majorityMaskPasses; i++) mask = majorityFilter(mask, w, h, majorityMaskThreshold)

  let maskExpanded = mask
  for (let i = 0; i < maskDilatePasses; i++) maskExpanded = dilate(maskExpanded, w, h)

  // XDoG edges (0/1)
  const xdogEdges = xdogBinary(gray, w, h, options)

  // Keep edges only where inside the (expanded) object.
  const combined = new Uint8ClampedArray(total)
  for (let i = 0; i < total; i++) {
    combined[i] = (xdogEdges[i] && maskExpanded[i]) ? 1 : 0
  }

  const includeBoundary = options.includeBoundary !== false
  if (includeBoundary) {
    const di = dilate(maskExpanded, w, h)
    const er = erode(maskExpanded, w, h)
    for (let i = 0; i < total; i++) {
      if (di[i] !== er[i]) combined[i] = 1
    }
  }

  return combined
}

function zhangSuenThinning(binary, w, h, maxIters = 3) {
  // Zhang–Suen thinning for 1-pixel wide-ish skeleton lines.
  // binary: Uint8ClampedArray with values 0/1 (1 = edge).
  // Performance: keep maxIters small (2-4) on large images.
  const b = new Uint8ClampedArray(binary)

  const idx = (x, y) => y * w + x
  const get = (x, y) => (x < 0 || x >= w || y < 0 || y >= h) ? 0 : (b[idx(x, y)] ? 1 : 0)

  let changed = true
  let iter = 0
  while (changed && iter < maxIters) {
    changed = false
    iter++

    // Step 1
    const toDelete1 = []
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        if (get(x, y) !== 1) continue

        const p2 = get(x, y - 1)      // N
        const p3 = get(x + 1, y - 1)  // NE
        const p4 = get(x + 1, y)      // E
        const p5 = get(x + 1, y + 1)  // SE
        const p6 = get(x, y + 1)      // S
        const p7 = get(x - 1, y + 1)  // SW
        const p8 = get(x - 1, y)      // W
        const p9 = get(x - 1, y - 1)  // NW

        const B = p2 + p3 + p4 + p5 + p6 + p7 + p8 + p9
        if (B < 2 || B > 6) continue

        const seq = [p2, p3, p4, p5, p6, p7, p8, p9, p2]
        let A = 0
        for (let k = 0; k < 8; k++) if (seq[k] === 0 && seq[k + 1] === 1) A++
        if (A !== 1) continue

        if (p2 * p4 * p6 !== 0) continue
        if (p4 * p6 * p8 !== 0) continue

        toDelete1.push(idx(x, y))
      }
    }
    if (toDelete1.length) changed = true
    for (const i of toDelete1) b[i] = 0

    // Step 2
    if (!changed) break
    const toDelete2 = []
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        if (get(x, y) !== 1) continue

        const p2 = get(x, y - 1)      // N
        const p3 = get(x + 1, y - 1)  // NE
        const p4 = get(x + 1, y)      // E
        const p5 = get(x + 1, y + 1)  // SE
        const p6 = get(x, y + 1)      // S
        const p7 = get(x - 1, y + 1)  // SW
        const p8 = get(x - 1, y)      // W
        const p9 = get(x - 1, y - 1)  // NW

        const B = p2 + p3 + p4 + p5 + p6 + p7 + p8 + p9
        if (B < 2 || B > 6) continue

        const seq = [p2, p3, p4, p5, p6, p7, p8, p9, p2]
        let A = 0
        for (let k = 0; k < 8; k++) if (seq[k] === 0 && seq[k + 1] === 1) A++
        if (A !== 1) continue

        if (p2 * p4 * p8 !== 0) continue
        if (p2 * p6 * p8 !== 0) continue

        toDelete2.push(idx(x, y))
      }
    }
    if (toDelete2.length) changed = true
    for (const i of toDelete2) b[i] = 0
  }

  return b
}

function connectNearbyEndpoints(binary, w, h, {
  maxDist = 18,
  maxConnections = 200,
  skeletonIters = 2,
  maxEndpoints = 1200,
} = {}) {
  // Connect broken "almost loops" by pairing skeleton endpoints
  // only when they are within `maxDist` pixels.
  // This helps close circles without connecting arbitrary far points.
  if (!binary || binary.length !== w * h) return binary

  const total = w * h
  // Avoid expensive endpoint detection on huge images.
  if (total > 1800000) return binary

  const maxDist2 = maxDist * maxDist
  const out = new Uint8ClampedArray(binary)

  const skeleton = zhangSuenThinning(out, w, h, Math.max(1, Math.round(skeletonIters)))

  const endpoints = []
  const neighborCount = (x, y) => {
    let c = 0
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue
        const xx = x + dx
        const yy = y + dy
        if (xx < 0 || xx >= w || yy < 0 || yy >= h) continue
        if (skeleton[yy * w + xx]) c++
      }
    }
    return c
  }

  // Only consider interior pixels; border endpoints are rare and easy to mis-detect.
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const idx = y * w + x
      if (!skeleton[idx]) continue
      if (neighborCount(x, y) === 1) endpoints.push({ x, y })
    }
  }

  if (endpoints.length < 2) return binary
  if (endpoints.length > maxEndpoints) return binary

  const used = new Uint8ClampedArray(endpoints.length)
  let connections = 0

  const setPixel = (x, y) => {
    if (x < 0 || x >= w || y < 0 || y >= h) return
    out[y * w + x] = 1
  }

  const drawLine = (x0, y0, x1, y1) => {
    // Bresenham line
    let dx = Math.abs(x1 - x0)
    let sx = x0 < x1 ? 1 : -1
    let dy = -Math.abs(y1 - y0)
    let sy = y0 < y1 ? 1 : -1
    let err = dx + dy

    while (true) {
      setPixel(x0, y0)
      if (x0 === x1 && y0 === y1) break
      const e2 = 2 * err
      if (e2 >= dy) { err += dy; x0 += sx }
      if (e2 <= dx) { err += dx; y0 += sy }
    }
  }

  for (let i = 0; i < endpoints.length; i++) {
    if (used[i]) continue
    const a = endpoints[i]
    let best = -1
    let bestD2 = maxDist2

    for (let j = i + 1; j < endpoints.length; j++) {
      if (used[j]) continue
      const b = endpoints[j]
      const dx = a.x - b.x
      const dy = a.y - b.y
      const d2 = dx * dx + dy * dy
      if (d2 <= bestD2) {
        bestD2 = d2
        best = j
      }
    }

    if (best !== -1) {
      const b = endpoints[best]
      drawLine(a.x, a.y, b.x, b.y)
      used[i] = 1
      used[best] = 1
      connections++
      if (connections >= maxConnections) break
    }
  }

  return out
}

function removeSmallComponents(binary, w, h, minSize = 10) {
  // Remove tiny islands ("dots") while keeping real strokes connected.
  if (!binary || binary.length !== w * h) return binary
  if (minSize <= 1) return binary

  const out = new Uint8ClampedArray(binary)
  const seen = new Uint8ClampedArray(binary.length)
  const stack = []
  const component = []

  const pushIfOn = (x, y) => {
    if (x < 0 || x >= w || y < 0 || y >= h) return
    const idx = y * w + x
    if (seen[idx] || !out[idx]) return
    seen[idx] = 1
    stack.push(idx)
  }

  for (let i = 0; i < out.length; i++) {
    if (!out[i] || seen[i]) continue

    stack.length = 0
    component.length = 0
    seen[i] = 1
    stack.push(i)

    while (stack.length) {
      const idx = stack.pop()
      component.push(idx)
      const x = idx % w
      const y = (idx - x) / w

      pushIfOn(x + 1, y)
      pushIfOn(x - 1, y)
      pushIfOn(x, y + 1)
      pushIfOn(x, y - 1)
      pushIfOn(x + 1, y + 1)
      pushIfOn(x - 1, y + 1)
      pushIfOn(x + 1, y - 1)
      pushIfOn(x - 1, y - 1)
    }

    if (component.length < minSize) {
      for (const idx of component) out[idx] = 0
    }
  }

  return out
}

function pruneSpurs(binary, w, h, iters = 1) {
  // Remove short dangling branches ("spurs") on a thin skeleton.
  // Endpoints in a 1-pixel skeleton have <= 1 neighbor in 8-neighborhood.
  let b = new Uint8ClampedArray(binary)

  const idx = (x, y) => y * w + x
  const get = (x, y) => {
    if (x < 0 || x >= w || y < 0 || y >= h) return 0
    return b[idx(x, y)] ? 1 : 0
  }

  const neighborCount = (x, y) => (
    get(x - 1, y - 1) + get(x, y - 1) + get(x + 1, y - 1) +
    get(x - 1, y) + get(x + 1, y) +
    get(x - 1, y + 1) + get(x, y + 1) + get(x + 1, y + 1)
  )

  for (let it = 0; it < Math.max(0, Math.round(iters)); it++) {
    const toRemove = []
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const id = idx(x, y)
        if (!b[id]) continue
        const deg = neighborCount(x, y)
        // Remove isolated pixels and endpoints; preserve junctions.
        if (deg <= 1) toRemove.push(id)
      }
    }
    if (!toRemove.length) break
    for (const id of toRemove) b[id] = 0
  }

  return b
}

function strokeBand(binary, w, h, passes = 1) {
  // Create a consistent "pen stroke" band around thin/thinned edges.
  // Band = dilate(binary) \ erode(binary).
  let d = binary
  for (let i = 0; i < Math.max(0, Math.round(passes)); i++) d = dilate(d, w, h)

  let e = binary
  for (let i = 0; i < Math.max(0, Math.round(passes)); i++) e = erode(e, w, h)

  const out = new Uint8ClampedArray(binary.length)
  for (let i = 0; i < out.length; i++) {
    out[i] = (d[i] && !e[i]) ? 1 : 0
  }
  return out
}

function otsuThreshold(values) {
  const hist = new Array(256).fill(0)
  for (let i = 0; i < values.length; i++) hist[values[i]]++
  const total = values.length
  let sum = 0
  for (let t = 0; t < 256; t++) sum += t * hist[t]
  let sumB = 0
  let wB = 0
  let wF = 0
  let varMax = 0
  let threshold = 45
  for (let t = 0; t < 256; t++) {
    wB += hist[t]
    if (wB === 0) continue
    wF = total - wB
    if (wF === 0) break
    sumB += t * hist[t]
    const mB = sumB / wB
    const mF = (sum - sumB) / wF
    const between = wB * wF * (mB - mF) * (mB - mF)
    if (between > varMax) { varMax = between; threshold = t }
  }
  return threshold
}

function dilate(binary, w, h) {
  const out = new Uint8ClampedArray(binary.length)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let on = 0
      for (let dy = -1; dy <= 1 && !on; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const yy = y + dy
          const xx = x + dx
          if (yy < 0 || yy >= h || xx < 0 || xx >= w) continue
          if (binary[yy * w + xx]) { on = 1; break }
        }
      }
      out[y * w + x] = on
    }
  }
  return out
}

function majorityFilter(binary, w, h, threshold = 5) {
  // Majority/median-like filter to remove tiny jaggies ("pixel bumps") on 1-pixel lines.
  // threshold=5 means: in a 3x3 neighborhood (9 pixels), keep 1 if >=5 are 1.
  const out = new Uint8ClampedArray(binary.length)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0
      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy
        if (yy < 0 || yy >= h) continue
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx
          if (xx < 0 || xx >= w) continue
          sum += binary[yy * w + xx] ? 1 : 0
        }
      }
      out[y * w + x] = sum >= threshold ? 1 : 0
    }
  }
  return out
}

function erode(binary, w, h) {
  const out = new Uint8ClampedArray(binary.length)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let on = 1
      for (let dy = -1; dy <= 1 && on; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const yy = y + dy
          const xx = x + dx
          if (yy < 0 || yy >= h || xx < 0 || xx >= w) { on = 0; break }
          if (!binary[yy * w + xx]) { on = 0; break }
        }
      }
      out[y * w + x] = on
    }
  }
  return out
}

function closeBinary(binary, w, h, passes = 1) {
  // Closing = dilate -> erode. Repeat a few times to connect tiny gaps ("holes").
  let out = binary
  for (let i = 0; i < passes; i++) {
    out = erode(dilate(out, w, h), w, h)
  }
  return out
}

function openBinary(binary, w, h, passes = 1) {
  // Opening = erode -> dilate. Repeat to remove tiny protrusions/spurs.
  let out = binary
  for (let i = 0; i < passes; i++) {
    out = dilate(erode(out, w, h), w, h)
  }
  return out
}

/**
 * Process image to coloring-book style: white background, black outlines only.
 * @param {HTMLCanvasElement} sourceCanvas - canvas with image drawn
 * @param {object} [options] - Outline tuning parameters (blur, thresholds, morphology).
 * @returns {HTMLCanvasElement} new canvas with outline image
 */
const CTX_READ_OFTEN = { willReadFrequently: true };

export function imageToOutlines(sourceCanvas, options = {}) {
  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  const ctx = sourceCanvas.getContext('2d', CTX_READ_OFTEN);
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  const pipeline = typeof options.pipeline === 'string' ? options.pipeline : 'canny' // 'canny' | 'shapeAware' | 'xdog' | 'objectContour' | 'objectXdog'

  const blurPasses = Math.max(1, Math.round(typeof options.blurPasses === 'number' ? options.blurPasses : 2))
  const hysteresisHighOffset = typeof options.hysteresisHighOffset === 'number' ? options.hysteresisHighOffset : 8
  const hysteresisHighMin = typeof options.hysteresisHighMin === 'number' ? options.hysteresisHighMin : 20
  const hysteresisHighMax = typeof options.hysteresisHighMax === 'number' ? options.hysteresisHighMax : 170
  const hysteresisLowRatio = typeof options.hysteresisLowRatio === 'number' ? options.hysteresisLowRatio : 0.5
  const hysteresisLowMin = typeof options.hysteresisLowMin === 'number' ? options.hysteresisLowMin : 10

  const closePasses = Math.max(0, Math.round(typeof options.closePasses === 'number' ? options.closePasses : 2))
  const erodePasses = Math.max(0, Math.round(typeof options.erodePasses === 'number' ? options.erodePasses : 1))
  const dilatePasses = Math.max(0, Math.round(typeof options.dilatePasses === 'number' ? options.dilatePasses : 1))
  const edgeSmoothPasses = Math.max(0, Math.round(typeof options.edgeSmoothPasses === 'number' ? options.edgeSmoothPasses : 1))
  const edgeSmoothThreshold = typeof options.edgeSmoothThreshold === 'number' ? options.edgeSmoothThreshold : 5
  const openPasses = Math.max(0, Math.round(typeof options.openPasses === 'number' ? options.openPasses : 0))
  const thinPasses = Math.max(0, Math.round(typeof options.thinPasses === 'number' ? options.thinPasses : 0))
  const pruneSpursIters = Math.max(0, Math.round(typeof options.pruneSpursIters === 'number' ? options.pruneSpursIters : 0))
  const postThinDilatePasses = Math.max(0, Math.round(typeof options.postThinDilatePasses === 'number' ? options.postThinDilatePasses : 0))
  const postThinMajorityPasses = Math.max(0, Math.round(typeof options.postThinMajorityPasses === 'number' ? options.postThinMajorityPasses : 0))
  const postThinMajorityThreshold = typeof options.postThinMajorityThreshold === 'number' ? options.postThinMajorityThreshold : edgeSmoothThreshold
  const strokeBandPasses = Math.max(0, Math.round(typeof options.strokeBandPasses === 'number' ? options.strokeBandPasses : 0))
  /** לפני close/dilate: הופך סרטי־קצה עבים (מילוי) לרצועת גבול — קווים במקום גושים */
  const strokeBandEarlyPasses = Math.max(0, Math.round(typeof options.strokeBandEarlyPasses === 'number' ? options.strokeBandEarlyPasses : 0))
  const finalClosePasses = Math.max(0, Math.round(typeof options.finalClosePasses === 'number' ? options.finalClosePasses : 0))
  const finalSmoothPasses = Math.max(0, Math.round(typeof options.finalSmoothPasses === 'number' ? options.finalSmoothPasses : 0))
  const finalSmoothThreshold = typeof options.finalSmoothThreshold === 'number' ? options.finalSmoothThreshold : edgeSmoothThreshold

  // 1. Grayscale
  const gray = new Uint8ClampedArray(w * h);
  for (let i = 0; i < data.length; i += 4) {
    const g = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    gray[i / 4] = g;
  }

  // 2. Gaussian blur to reduce noise while keeping edges
  // Blur is conceptually like diffusion / heat equation steps: more passes -> cleaner regions.
  let blurred = gray
  for (let i = 0; i < blurPasses; i++) blurred = gaussianBlurGray(blurred, w, h)

  // Special pipeline: XDoG-style for cleaner line drawings from photos (suppresses textures/shading).
  // We still run the same morphology cleanup later to connect gaps and smooth jaggies.
  let xdogBinaryEdges = null
  if (pipeline === 'xdog') {
    xdogBinaryEdges = xdogBinary(blurred, w, h, options)
  }

  let objectContourEdges = null
  if (pipeline === 'objectContour') {
    objectContourEdges = objectContourBinary(blurred, w, h, options)
  }

  let objectXdogEdges = null
  if (pipeline === 'objectXdog') {
    objectXdogEdges = objectXdogBinary(blurred, w, h, options)
  }

  // 3. Build RGBA for Sobel (reuse structure)
  const grayRgba = new Uint8ClampedArray(w * h * 4);
  for (let i = 0; i < w * h; i++) {
    grayRgba[i * 4] = blurred[i];
    grayRgba[i * 4 + 1] = blurred[i];
    grayRgba[i * 4 + 2] = blurred[i];
    grayRgba[i * 4 + 3] = 255;
  }

  // Signed gradients so we can do Non-Maximum Suppression by gradient direction.
  const gx = convolveSigned(grayRgba, w, h, SOBEL_X)
  const gy = convolveSigned(grayRgba, w, h, SOBEL_Y)

  // 4. Magnitude -> normalize
  const magsNorm = new Uint8ClampedArray(w * h)
  let maxMag = 1
  for (let i = 0; i < w * h; i++) {
    const m = Math.sqrt(gx[i] * gx[i] + gy[i] * gy[i])
    if (m > maxMag) maxMag = m
  }
  for (let i = 0; i < w * h; i++) {
    magsNorm[i] = Math.min(255, Math.round((Math.sqrt(gx[i] * gx[i] + gy[i] * gy[i]) / maxMag) * 255))
  }

  let edges

  if (pipeline === 'objectContour') {
    edges = objectContourEdges || new Uint8ClampedArray(w * h)
  } else if (pipeline === 'objectXdog') {
    edges = objectXdogEdges || new Uint8ClampedArray(w * h)
  } else if (pipeline === 'xdog') {
    edges = xdogBinaryEdges || new Uint8ClampedArray(w * h)
  } else if (pipeline === 'shapeAware') {
    // Shape-aware mode:
    // Instead of "thin edges only" (NMS + strict hysteresis), keep more candidates
    // based on gradient magnitude, then rely on morphology to connect and thicken.
    const base = otsuThreshold(magsNorm)
    const shapeOffset = typeof options.shapeOffset === 'number' ? options.shapeOffset : 12
    const thr = Math.max(10, Math.min(200, base - shapeOffset))

    edges = new Uint8ClampedArray(w * h)
    for (let i = 0; i < w * h; i++) edges[i] = magsNorm[i] >= thr ? 1 : 0
  } else {
    // Canny-like mode:
    // 4b. Non-Maximum Suppression (keep only local maxima along gradient direction)
    const nms = new Uint8ClampedArray(w * h)
    const TAN_22_5 = 0.41421356237
    const TAN_67_5 = 2.41421356237
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const idx = y * w + x
        const mag = magsNorm[idx]
        if (mag === 0) continue

        const absGx = Math.abs(gx[idx])
        const absGy = Math.abs(gy[idx])

        // Determine which of the 4 principal directions (0,45,90,135) to use.
        let idx1 = 0
        let idx2 = 0
        if (absGy <= absGx * TAN_22_5) {
          // 0 degrees (left-right)
          idx1 = idx - 1
          idx2 = idx + 1
        } else if (absGy >= absGx * TAN_67_5) {
          // 90 degrees (up-down)
          idx1 = idx - w
          idx2 = idx + w
        } else if (gx[idx] * gy[idx] > 0) {
          // 45 degrees (top-right / bottom-left)
          idx1 = idx - w + 1
          idx2 = idx + w - 1
        } else {
          // 135 degrees (top-left / bottom-right)
          idx1 = idx - w - 1
          idx2 = idx + w + 1
        }

        if (mag >= magsNorm[idx1] && mag >= magsNorm[idx2]) nms[idx] = mag
      }
    }

    // 4c. Hysteresis thresholding:
    const base = otsuThreshold(nms)
    const highThr = Math.max(hysteresisHighMin, Math.min(hysteresisHighMax, base + hysteresisHighOffset))
    const lowThr = Math.max(hysteresisLowMin, Math.round(highThr * hysteresisLowRatio))

    edges = new Uint8ClampedArray(w * h)
    const stack = []

    for (let i = 0; i < w * h; i++) {
      if (nms[i] >= highThr) {
        edges[i] = 1
        stack.push(i)
      }
    }

    // Flood-fill from strong edges to keep weak edges connected to them.
    while (stack.length) {
      const idx = stack.pop()
      const x = idx % w
      const y = (idx - x) / w

      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy
        if (yy < 0 || yy >= h) continue
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx
          if (xx < 0 || xx >= w) continue
          if (dx === 0 && dy === 0) continue

          const ni = yy * w + xx
          if (edges[ni] === 0 && nms[ni] >= lowThr) {
            edges[ni] = 1
            stack.push(ni)
          }
        }
      }
    }
  }

  // Optional: keep only edges inside the "main object" region.
  // This reduces background noise while still allowing internal details.
  if (options.objectMaskToEdges === true) {
    const total = w * h
    const maskDilatePasses = Math.max(0, Math.round(typeof options.maskDilatePasses === 'number' ? options.maskDilatePasses : 1))

    if (options.objectMaskBinary && options.objectMaskBinary.length === total) {
      // Deeplab/ML-provided binary mask already in input resolution.
      let maskExpanded = new Uint8ClampedArray(options.objectMaskBinary)
      for (let i = 0; i < maskDilatePasses; i++) maskExpanded = dilate(maskExpanded, w, h)
      for (let i = 0; i < total; i++) edges[i] = (edges[i] && maskExpanded[i]) ? 1 : 0
    } else {
      const maskThresholdScale = typeof options.maskThresholdScale === 'number' ? options.maskThresholdScale : 1.03
      const thrMask = otsuThreshold(blurred)
      const tMask = Math.max(1, Math.min(254, Math.round(thrMask * maskThresholdScale)))

      let mask = new Uint8ClampedArray(total)
      let onCount = 0
      for (let i = 0; i < total; i++) {
        const on = blurred[i] < tMask ? 1 : 0
        mask[i] = on
        onCount += on
      }

      // Invert if segmentation is clearly on the wrong side.
      if (onCount < total * 0.05 || onCount > total * 0.95) {
        for (let i = 0; i < total; i++) mask[i] = mask[i] ? 0 : 1
      }

      let maskExpanded = mask
      for (let i = 0; i < maskDilatePasses; i++) maskExpanded = dilate(maskExpanded, w, h)

      for (let i = 0; i < total; i++) edges[i] = (edges[i] && maskExpanded[i]) ? 1 : 0
    }
  }

  // 5. Cleanup:
  // - close multiple times to connect broken lines + reduce speckles ("fill holes")
  // - a small thin-back step to make contours feel sharper
  let cleaned = edges
  if (strokeBandEarlyPasses > 0) {
    cleaned = strokeBand(cleaned, w, h, strokeBandEarlyPasses)
  }
  if (closePasses > 0) cleaned = closeBinary(cleaned, w, h, closePasses)
  for (let i = 0; i < erodePasses; i++) cleaned = erode(cleaned, w, h)
  for (let i = 0; i < dilatePasses; i++) cleaned = dilate(cleaned, w, h)
  for (let i = 0; i < edgeSmoothPasses; i++) cleaned = majorityFilter(cleaned, w, h, edgeSmoothThreshold)
  if (openPasses > 0) cleaned = openBinary(cleaned, w, h, openPasses)

  if (thinPasses > 0) {
    // Make edges thinner (reduces "fills" into blobs) while preserving connectivity.
    cleaned = zhangSuenThinning(cleaned, w, h, thinPasses)
  }

  if (pruneSpursIters > 0) {
    cleaned = pruneSpurs(cleaned, w, h, pruneSpursIters)
  }

  // If we want a "pen/pencil" look, re-thicken slightly after thinning,
  // but keep it controlled to avoid blobs/fills.
  for (let i = 0; i < postThinDilatePasses; i++) cleaned = dilate(cleaned, w, h)
  for (let i = 0; i < postThinMajorityPasses; i++) cleaned = majorityFilter(cleaned, w, h, postThinMajorityThreshold)

  if (strokeBandPasses > 0) {
    cleaned = strokeBand(cleaned, w, h, strokeBandPasses)
  }

  if (options.connectEndpoints === true) {
    const maxDist = typeof options.endpointMaxDist === 'number' ? options.endpointMaxDist : 18
    const maxConnections = typeof options.endpointMaxConnections === 'number' ? options.endpointMaxConnections : 200
    const skeletonIters = typeof options.endpointSkeletonIters === 'number' ? options.endpointSkeletonIters : 2
    cleaned = connectNearbyEndpoints(cleaned, w, h, { maxDist, maxConnections, skeletonIters })

    const postConnectThinIters = Math.max(0, Math.round(typeof options.postConnectThinIters === 'number' ? options.postConnectThinIters : 0))
    if (postConnectThinIters > 0) {
      cleaned = zhangSuenThinning(cleaned, w, h, postConnectThinIters)
    }
  }

  const minComponentSize = Math.max(0, Math.round(typeof options.minComponentSize === 'number' ? options.minComponentSize : 0))
  if (minComponentSize > 1) {
    cleaned = removeSmallComponents(cleaned, w, h, minComponentSize)
  }

  if (finalClosePasses > 0) {
    cleaned = closeBinary(cleaned, w, h, finalClosePasses)
  }

  // Final anti-jaggy pass for smoother "swirls"/strokes.
  for (let i = 0; i < finalSmoothPasses; i++) {
    cleaned = majorityFilter(cleaned, w, h, finalSmoothThreshold)
  }

  const result = new ImageData(w, h)
  for (let i = 0; i < w * h; i++) {
    const isEdge = cleaned[i] ? 0 : 255
    result.data[i * 4] = isEdge
    result.data[i * 4 + 1] = isEdge
    result.data[i * 4 + 2] = isEdge
    result.data[i * 4 + 3] = 255
  }

  const outCanvas = document.createElement('canvas');
  outCanvas.width = w;
  outCanvas.height = h;
  outCanvas.getContext('2d', CTX_READ_OFTEN).putImageData(result, 0, 0);
  return outCanvas;
}

/**
 * למצלמה: מאחד תוצאות edge לשחור-לבן נקי. מונע תרחישים שבהם נשאר רק בהיר על בהיר
 * (לאחר שנמחק דיו בשלב prep, או לפני overlay).
 * משתמש ב־Otsu על luminance ובוחר היכן ה«דיו» לפי איזה מחצית מהתמונה אמור להיות הרקע.
 * @param {HTMLCanvasElement} outlineCanvas — מקבל אליאס grayscale, לכתוש in-place ל־#00 / #ff
 */
export function quantizeOutlineInkForCamera(outlineCanvas) {
  const w = outlineCanvas.width
  const h = outlineCanvas.height
  const ctx = outlineCanvas.getContext('2d', CTX_READ_OFTEN)
  if (!ctx || w < 2 || h < 2) return outlineCanvas
  const n = w * h
  const imageData = ctx.getImageData(0, 0, w, h)
  const d = imageData.data
  const lums = new Uint8ClampedArray(n)
  for (let i = 0; i < n; i++) {
    const o = i * 4
    lums[i] = Math.round((d[o] * 299 + d[o + 1] * 587 + d[o + 2] * 114) / 1000)
  }
  const t = Math.max(1, Math.min(254, otsuThreshold(lums)))
  let below = 0
  let sumBelow = 0
  let sumAbove = 0
  let globalSum = 0
  for (let i = 0; i < n; i++) {
    const L = lums[i]
    globalSum += L
    if (L < t) {
      below++
      sumBelow += L
    } else {
      sumAbove += L
    }
  }
  const cntBelow = below
  const cntAbove = n - cntBelow
  const belowRatio = below / n
  const meanBelow = cntBelow > 0 ? sumBelow / cntBelow : 0
  const meanAbove = cntAbove > 0 ? sumAbove / cntAbove : 255
  const globalMean = globalSum / n

  /**
   * ברירת מחדל: מיעוט כהה = קווים (דיו כהה). רוב כהה = קווים בהירים על רקע כהה → דיו בצד הבהיר.
   * ליד 50/50 Otsu עלול לטעות — משתמשים בממוצע כללי ובמרחק מהסף.
   */
  let inkIsDark
  if (belowRatio < 0.42) {
    inkIsDark = true
  } else if (belowRatio > 0.58) {
    inkIsDark = false
  } else if (Math.abs(belowRatio - 0.5) <= 0.035) {
    inkIsDark = globalMean > 125
  } else {
    inkIsDark = belowRatio < 0.5
  }
  /** מיעוט כהה מול Otsu אמור להיות דיו כהה; אם סומנו קווים בהירים בטעות — מתקנים */
  if (cntBelow > 0 && cntAbove > 0) {
    const darkerIsBelow = meanBelow < meanAbove
    if (!inkIsDark && darkerIsBelow && belowRatio < 0.5) inkIsDark = true
  }

  for (let i = 0; i < n; i++) {
    const darkSide = lums[i] < t
    const isInk = inkIsDark ? darkSide : !darkSide
    const v = isInk ? 0 : 255
    const o = i * 4
    d[o] = v
    d[o + 1] = v
    d[o + 2] = v
    d[o + 3] = 255
  }

  let blackCount = 0
  for (let i = 0; i < n; i++) {
    const o = i * 4
    if (d[o] < 128) blackCount++
  }
  if (blackCount / n > 0.56) {
    for (let i = 0; i < d.length; i += 4) {
      const v = 255 - d[i]
      d[i] = v
      d[i + 1] = v
      d[i + 2] = v
    }
  }

  ctx.putImageData(imageData, 0, 0)
  return outlineCanvas
}

/**
 * צילום → דף צביעה: מאלץ קווים טהורים #000 ונייר #fff.
 * `outlineToOverlay` סופר כאטום רק lum &lt; 172 — דיו אפור/בהיר נעלם ונראה כמו "קווים לבנים".
 * כאן מסתמכים על בהירות ממוצעת בשוליים (נייר) ובודקים קוטב מול רוב שחור.
 * @param {HTMLCanvasElement} outlineCanvas
 */
export function ensureCameraOutlineBlackOnWhite(outlineCanvas) {
  const w = outlineCanvas.width
  const h = outlineCanvas.height
  const ctx = outlineCanvas.getContext('2d', CTX_READ_OFTEN)
  if (!ctx || w < 4 || h < 4) return outlineCanvas

  const imageData = ctx.getImageData(0, 0, w, h)
  const d = imageData.data
  const n = w * h
  const lums = new Uint8ClampedArray(n)
  for (let i = 0; i < n; i++) {
    const o = i * 4
    lums[i] = Math.round((d[o] * 299 + d[o + 1] * 587 + d[o + 2] * 114) / 1000)
  }

  const strip = Math.max(1, Math.min(8, Math.floor(Math.min(w, h) * 0.028)))
  let borderSum = 0
  let borderCount = 0
  const addBorder = (x, y) => {
    if (x >= 0 && x < w && y >= 0 && y < h) {
      borderSum += lums[y * w + x]
      borderCount++
    }
  }
  for (let t = 0; t < strip; t++) {
    for (let x = 0; x < w; x++) {
      addBorder(x, t)
      addBorder(x, h - 1 - t)
    }
    for (let y = strip; y < h - strip; y++) {
      addBorder(t, y)
      addBorder(w - 1 - t, y)
    }
  }
  const borderMean = borderCount ? borderSum / borderCount : 248

  const hist = new Uint32Array(256)
  for (let i = 0; i < n; i++) hist[lums[i]]++
  /** p=0.9 → כ־90% מהפיקסלים בהירים עד ערך זה (מוערך "נייר") */
  const lumPercentileAsc = (p) => {
    const target = Math.max(1, Math.min(n, Math.floor(p * n)))
    let acc = 0
    for (let g = 0; g < 256; g++) {
      acc += hist[g]
      if (acc >= target) return g
    }
    return 255
  }
  const pPaper = lumPercentileAsc(0.9)
  const paperRef = Math.max(borderMean, pPaper)

  if (paperRef >= 108) {
    const inkTh = Math.round(Math.max(22, Math.min(220, paperRef - 34)))
    for (let i = 0; i < n; i++) {
      const o = i * 4
      const ink = lums[i] <= inkTh
      const v = ink ? 0 : 255
      d[o] = v
      d[o + 1] = v
      d[o + 2] = v
      d[o + 3] = 255
    }
  } else {
    const t = Math.max(1, Math.min(254, otsuThreshold(lums)))
    let below = 0
    for (let j = 0; j < n; j++) {
      if (lums[j] < t) below++
    }
    const inkDark = below / n <= 0.5
    for (let i = 0; i < n; i++) {
      const o = i * 4
      const dark = lums[i] < t
      const ink = inkDark ? dark : !dark
      const v = ink ? 0 : 255
      d[o] = v
      d[o + 1] = v
      d[o + 2] = v
      d[o + 3] = 255
    }
  }

  let blackCount = 0
  for (let i = 0; i < d.length; i += 4) {
    if (d[i] < 128) blackCount++
  }
  if (blackCount / n > 0.52) {
    for (let i = 0; i < d.length; i += 4) {
      const v = 255 - d[i]
      d[i] = v
      d[i + 1] = v
      d[i + 2] = v
    }
  }

  ctx.putImageData(imageData, 0, 0)
  return outlineCanvas
}

/**
 * הפיכת שכבת outline לגרסת "דף צביעה" ידידותית להדפסה:
 * בינאריזציה נקייה, סגירת רווחים זעירים, עיבוי קו עדין, ואיזון עדין כדי לא להפוך לכתמים.
 * @param {HTMLCanvasElement} outlineCanvas
 * @param {object} [options]
 * @param {number} [options.gapClosePasses=2]
 * @param {number} [options.lineThickenPasses=2]
 * @param {number} [options.balanceErodePasses=1]
 */
export function finalizePrintableColoringOutline(outlineCanvas, options = {}) {
  const w = outlineCanvas.width
  const h = outlineCanvas.height
  const ctx = outlineCanvas.getContext('2d', CTX_READ_OFTEN)
  if (!ctx || w < 2 || h < 2) return outlineCanvas
  const gapClose = Math.max(0, Math.min(4, Math.round(options.gapClosePasses ?? 2)))
  const thicken = Math.max(0, Math.min(3, Math.round(options.lineThickenPasses ?? 2)))
  const balanceErode = Math.max(0, Math.min(2, Math.round(options.balanceErodePasses ?? 1)))

  const imageData = ctx.getImageData(0, 0, w, h)
  const d = imageData.data
  const n = w * h
  let bin = new Uint8ClampedArray(n)
  for (let i = 0; i < n; i++) {
    const o = i * 4
    const lum = (d[o] + d[o + 1] + d[o + 2]) / 3
    bin[i] = lum < 128 ? 1 : 0
  }
  if (gapClose > 0) bin = closeBinary(bin, w, h, gapClose)
  for (let p = 0; p < thicken; p++) bin = dilate(bin, w, h)
  for (let p = 0; p < balanceErode; p++) bin = erode(bin, w, h)

  for (let i = 0; i < n; i++) {
    const v = bin[i] ? 0 : 255
    const o = i * 4
    d[o] = v
    d[o + 1] = v
    d[o + 2] = v
    d[o + 3] = 255
  }
  ctx.putImageData(imageData, 0, 0)
  return outlineCanvas
}

/**
 * קווי מתאר צריכים להיות שחורים על רקע לבן. אם יצא הפוך (קווים בהירים על שחור),
 * הופכים כך שהרקע הופך בהיר והקווים כהים.
 *
 * `imageToOutlines` מייצר כבר שחור-על-לבן; רק ספים שגויים כאן הפכו דפים צפופי-קווים
 * (צילום עמוס קצוות) ויצרו "קווים לבנים" ב-webview. לכן לא משתמשים ב־darkRatio בלבד.
 * @param {HTMLCanvasElement} outlineCanvas
 * @returns {HTMLCanvasElement}
 */
export function normalizeOutlineCanvasPolarity(outlineCanvas) {
  const w = outlineCanvas.width
  const h = outlineCanvas.height
  const ctx = outlineCanvas.getContext('2d', CTX_READ_OFTEN)
  if (!ctx || w < 2 || h < 2) return outlineCanvas

  const imageData = ctx.getImageData(0, 0, w, h)
  const data = imageData.data
  const px = data.length >> 2
  let lumSum = 0
  let darkCount = 0
  let lightCount = 0
  const darkTh = 140
  const lightTh = 210
  for (let i = 0; i < data.length; i += 4) {
    const lum = (data[i] + data[i + 1] + data[i + 2]) / 3
    lumSum += lum
    if (lum < darkTh) darkCount++
    if (lum > lightTh) lightCount++
  }
  const meanLum = lumSum / px
  const darkRatio = darkCount / px
  const lightRatio = lightCount / px

  // רוב הנייר הבהיר אחרי גילוי קצוות — אל תיפרד; אחרת נהפוך שחור-על-לבן תקין ונקבל קווים לבנים.
  if (lightRatio >= 0.30) return outlineCanvas

  // מסמך כהה כמעט לחלוטין (קווים בהירים על שחור) — היפוך נדרש לפני overlay.
  let invert = meanLum < 108 && lightRatio < 0.26
  if (!invert && meanLum < 128 && lightRatio < 0.22 && darkRatio > 0.55) invert = true

  if (!invert) return outlineCanvas

  for (let i = 0; i < data.length; i += 4) {
    const v = 255 - data[i]
    data[i] = v
    data[i + 1] = v
    data[i + 2] = v
  }
  ctx.putImageData(imageData, 0, 0)
  return outlineCanvas
}

function imageDataToOverlayCanvas(src) {
  const w = src.width
  const h = src.height
  const data = src.data
  const out = new ImageData(w, h)
  for (let i = 0; i < data.length; i += 4) {
    const lum = (data[i] + data[i + 1] + data[i + 2]) / 3
    // קווים אטומים שחורים — נראים על קנבס לבן בכל WebView (בלי שקיפות נמוכה)
    if (lum < 200) {
      out.data[i] = 0
      out.data[i + 1] = 0
      out.data[i + 2] = 0
      out.data[i + 3] = 255
    } else {
      out.data[i] = 0
      out.data[i + 1] = 0
      out.data[i + 2] = 0
      out.data[i + 3] = 0
    }
  }
  const outCanvas = document.createElement('canvas')
  outCanvas.width = w
  outCanvas.height = h
  outCanvas.getContext('2d', CTX_READ_OFTEN).putImageData(out, 0, 0)
  return outCanvas
}

function overlayOpaqueFraction(overlayCanvas) {
  const w = overlayCanvas.width
  const h = overlayCanvas.height
  const d = overlayCanvas.getContext('2d', CTX_READ_OFTEN).getImageData(0, 0, w, h).data
  let c = 0
  const n = w * h
  for (let i = 3; i < d.length; i += 4) {
    if (d[i] > 24) c++
  }
  return c / n
}

/**
 * Create overlay from outline: black lines on transparent background.
 * Used as a layer on top of the color layer so outline cannot be painted over.
 * אם הפולאריות הפוכה (רקע כהה נקלט כ"קווים"), רוב הפיקסלים בשכבה יוצאים אטומים —
 * מתקנים על ידי ניסיון היפוך שכפול של ה־raster לפני המרה.
 * @param {HTMLCanvasElement} outlineCanvas - canvas with black on white outline
 * @returns {HTMLCanvasElement} canvas with black pixels, rest transparent
 */
export function outlineToOverlay(outlineCanvas) {
  const w = outlineCanvas.width
  const h = outlineCanvas.height
  const ctx = outlineCanvas.getContext('2d', CTX_READ_OFTEN)
  const imageData = ctx.getImageData(0, 0, w, h)

  let best = imageDataToOverlayCanvas(imageData)
  let f = overlayOpaqueFraction(best)

  const tryInvertedSource = () => {
    const copy = new ImageData(new Uint8ClampedArray(imageData.data), w, h)
    const d = copy.data
    for (let i = 0; i < d.length; i += 4) {
      const v = 255 - d[i]
      d[i] = v
      d[i + 1] = v
      d[i + 2] = v
    }
    return imageDataToOverlayCanvas(copy)
  }

  // קווי מתאר אמיתיים = מיעוט אטום; כיסוי > ~75% = כנראה רקע כהה הוטעה כקווים.
  if (f > 0.74) {
    const alt = tryInvertedSource()
    const f2 = overlayOpaqueFraction(alt)
    if (f2 < f - 0.06) best = alt
  } else if (f < 0.0012) {
    const alt = tryInvertedSource()
    const f2 = overlayOpaqueFraction(alt)
    if (f2 > f + 0.004) best = alt
  }

  return best
}

/**
 * Create a thicker "barrier" version of outline for the fill tool.
 * The fill tool uses `isOutline()` based on `outlineDataRef`, so we can
 * dilate/close the outline mask to reduce leaks through tiny gaps.
 *
 * @param {HTMLCanvasElement} outlineCanvas - black lines on white
 * @param {object} [options]
 * @param {number} [options.lineLumThreshold=80] - lum < threshold is treated as line
 * @param {number} [options.dilatePasses=2] - how much to dilate the line barrier
 * @param {number} [options.closePasses=1] - optional closing to connect micro gaps
 * @param {number} [options.finalErodePasses=0] - optional erode at the end to control thickness
 * @returns {ImageData}
 */
export function outlineToFillBarrier(outlineCanvas, {
  lineLumThreshold = 80,
  dilatePasses = 2,
  closePasses = 1,
  finalErodePasses = 0,
} = {}) {
  const w = outlineCanvas.width
  const h = outlineCanvas.height
  const ctx = outlineCanvas.getContext('2d', CTX_READ_OFTEN)
  const imageData = ctx.getImageData(0, 0, w, h)
  const data = imageData.data
  const total = w * h

  let binary = new Uint8ClampedArray(total)
  for (let i = 0; i < total; i++) {
    const p = i * 4
    const lum = (data[p] + data[p + 1] + data[p + 2]) / 3
    binary[i] = lum < lineLumThreshold ? 1 : 0
  }

  for (let i = 0; i < Math.max(0, Math.round(dilatePasses)); i++) binary = dilate(binary, w, h)
  if (closePasses > 0) binary = closeBinary(binary, w, h, closePasses)
  for (let i = 0; i < Math.max(0, Math.round(finalErodePasses)); i++) binary = erode(binary, w, h)

  const out = new ImageData(w, h)
  for (let i = 0; i < total; i++) {
    const isEdge = binary[i] ? 0 : 255
    const p = i * 4
    out.data[p] = isEdge
    out.data[p + 1] = isEdge
    out.data[p + 2] = isEdge
    out.data[p + 3] = 255
  }
  return out
}

function polygonPath(ctx, cx, cy, radius, sides, rotation = -Math.PI / 2) {
  ctx.beginPath()
  for (let i = 0; i < sides; i++) {
    const a = rotation + (i * 2 * Math.PI) / sides
    const x = cx + radius * Math.cos(a)
    const y = cy + radius * Math.sin(a)
    if (i === 0) ctx.moveTo(x, y)
    else ctx.lineTo(x, y)
  }
  ctx.closePath()
}

/**
 * Create geometric outline canvas (ready for coloring, no edge-detection needed).
 * @param {string} shapeId - 'circle'|'square'|'triangle'|'rectangle'|'pentagon'|'hexagon'|'diamond'
 * @param {object} options
 * @param {number} options.size - canvas size
 * @param {number} options.padding - 0..0.35 padding fraction
 * @param {number} options.lineWidth - stroke width in px
 */
export function createGeometricOutlineCanvas(shapeId, { size = 512, padding = 0.14, lineWidth = 6 } = {}) {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, size, size)
  ctx.strokeStyle = '#000000'
  ctx.lineWidth = Math.max(2, Math.round(lineWidth))
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'

  const cx = size / 2
  const cy = size / 2
  const pad = Math.max(0, Math.min(0.35, padding))
  const r = (size * (1 - pad * 2)) / 2

  switch (shapeId) {
    case 'circle':
      ctx.beginPath()
      ctx.arc(cx, cy, r, 0, 2 * Math.PI)
      ctx.stroke()
      break
    case 'square':
      ctx.strokeRect(cx - r, cy - r, r * 2, r * 2)
      break
    case 'rectangle': {
      const rw = r * 2
      const rh = r * 1.35
      ctx.strokeRect(cx - rw / 2, cy - rh / 2, rw, rh)
      break
    }
    case 'triangle':
      polygonPath(ctx, cx, cy, r, 3)
      ctx.stroke()
      break
    case 'pentagon':
      polygonPath(ctx, cx, cy, r, 5)
      ctx.stroke()
      break
    case 'hexagon':
      polygonPath(ctx, cx, cy, r, 6)
      ctx.stroke()
      break
    case 'diamond':
      ctx.beginPath()
      ctx.moveTo(cx, cy - r)
      ctx.lineTo(cx + r, cy)
      ctx.lineTo(cx, cy + r)
      ctx.lineTo(cx - r, cy)
      ctx.closePath()
      ctx.stroke()
      break
    default:
      ctx.strokeRect(cx - r, cy - r, r * 2, r * 2)
  }

  return canvas
}

/**
 * Create a canvas with a single character (letter or number) for default coloring pages.
 * Draws the character in black on white, centered; ready for imageToOutlines.
 * @param {string} char - One character: A-Z, א-ת, or 1-10
 * @param {number} size - Canvas width/height (default 512)
 * @returns {HTMLCanvasElement}
 */
export function createCharacterCanvas(char, size = 512) {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, size, size)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const fontPx = size * 0.52
  ctx.font = `bold ${fontPx}px "Heebo", "Arial Hebrew", sans-serif`
  const x = size / 2
  const y = size / 2
  ctx.lineWidth = Math.max(4, fontPx * 0.08)
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.strokeStyle = '#000000'
  ctx.fillStyle = '#000000'
  ctx.strokeText(char, x, y)
  ctx.fillText(char, x, y)
  return canvas
}

/** Columns and cell size for ABC full page */
const ABC_COLS = 7
const ABC_CELL = 140
const ABC_PADDING = 10

/**
 * Create one canvas with all letters A–Z for coloring (one page).
 * @returns {HTMLCanvasElement} canvas with black letters on white, ready for imageToOutlines
 */
export function createAbcCanvas() {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
  const cols = ABC_COLS
  const rows = Math.ceil(letters.length / cols)
  const cell = ABC_CELL
  const pad = ABC_PADDING
  const w = cols * cell
  const h = rows * cell
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, w, h)
  ctx.fillStyle = '#000000'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const fontSize = Math.floor((cell - pad * 2) * 0.65)
  ctx.font = `bold ${fontSize}px "Heebo", "Arial", sans-serif`
  ctx.lineWidth = Math.max(3, fontSize * 0.07)
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.strokeStyle = '#000000'
  letters.forEach((char, i) => {
    const col = i % cols
    const row = Math.floor(i / cols)
    const x = col * cell + cell / 2
    const y = row * cell + cell / 2
    ctx.strokeText(char, x, y)
    ctx.fillText(char, x, y)
  })
  return canvas
}

/** 22 אותיות עבריות — סדר אל״ף-בית */
const HEBREW_ALPHABET_PAGE_LETTERS = 'אבגדהוזחטיכלמנסעפצקרשת'.split('')

/**
 * דף אלף-בית עברי — כל האותיות בקנבס אחד (RTL לשורה).
 * @returns {HTMLCanvasElement}
 */
export function createHebrewAlphabetCanvas() {
  const letters = [...HEBREW_ALPHABET_PAGE_LETTERS]
  const cols = ABC_COLS
  const rows = Math.ceil(letters.length / cols)
  const cell = ABC_CELL
  const pad = ABC_PADDING
  const w = cols * cell
  const h = rows * cell
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, w, h)
  ctx.fillStyle = '#000000'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const fontSize = Math.floor((cell - pad * 2) * 0.58)
  ctx.font = `bold ${fontSize}px "Varela Round", "Heebo", "Arial Hebrew", sans-serif`
  ctx.lineWidth = Math.max(3, fontSize * 0.07)
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.strokeStyle = '#000000'
  letters.forEach((char, i) => {
    const colRtl = cols - 1 - (i % cols)
    const row = Math.floor(i / cols)
    const x = colRtl * cell + cell / 2
    const y = row * cell + cell / 2
    ctx.strokeText(char, x, y)
    ctx.fillText(char, x, y)
  })
  return canvas
}

/** גובה קנבס וריפוד לדף מילה */
const WORD_PAGE_HEIGHT = 420
const WORD_PAGE_PADDING = 60
const WORD_FONT_SIZE_MAX = 200

/** גופני בלון: עגולים ועבים — אנגלית Fredoka, עברית Varela Round */
const BALLOON_FONT_LATIN = '"Fredoka", "Varela Round", sans-serif'
const BALLOON_FONT_HEBREW = '"Varela Round", "Heebo", "Arial Hebrew", sans-serif'

/**
 * Create a canvas with one word in balloon/bubble letters for coloring.
 * RTL-aware; single line, centered; rounded thick outline style.
 * @param {string} word - The word to draw (e.g. "שלום" or "שם שלי")
 * @returns {HTMLCanvasElement}
 */
export function createWordCanvas(word) {
  const text = (word || '').trim()
  if (!text) throw new Error('מילה ריקה')
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const isRtl = /[\u0590-\u05FF]/.test(text)
  const dir = isRtl ? 'rtl' : 'ltr'
  const fontFamily = isRtl ? BALLOON_FONT_HEBREW : BALLOON_FONT_LATIN
  const maxW = 1000
  let fontSize = WORD_FONT_SIZE_MAX
  ctx.font = `600 ${fontSize}px ${fontFamily}`
  let metrics = ctx.measureText(text)
  while (metrics.width > maxW - WORD_PAGE_PADDING * 2 && fontSize > 40) {
    fontSize -= 20
    ctx.font = `600 ${fontSize}px ${fontFamily}`
    metrics = ctx.measureText(text)
  }
  const w = Math.ceil(Math.min(metrics.width + WORD_PAGE_PADDING * 2, maxW))
  const h = WORD_PAGE_HEIGHT
  canvas.width = w
  canvas.height = h
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, w, h)
  ctx.fillStyle = '#000000'
  ctx.textAlign = isRtl ? 'right' : 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `600 ${fontSize}px ${fontFamily}`
  ctx.direction = dir
  const x = isRtl ? w - WORD_PAGE_PADDING : w / 2
  const y = h / 2
  // אותיות בלון: קו עבה ומעוגל סביב האותיות
  ctx.lineWidth = Math.max(8, fontSize * 0.08)
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.strokeStyle = '#000000'
  ctx.strokeText(text, x, y)
  ctx.fillText(text, x, y)
  return canvas
}

/**
 * Create a canvas with a simple shape for template gallery (black outline on white).
 * @param {string} shapeId - 'heart' | 'star' | 'flower' | 'house' | 'sun' | 'fish' | 'butterfly'
 * @param {number} size - canvas size (default 512)
 * @returns {HTMLCanvasElement}
 */
export function createTemplateShapeCanvas(shapeId, size = 512) {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, size, size)
  ctx.strokeStyle = '#000000'
  ctx.fillStyle = '#000000'
  ctx.lineWidth = Math.max(2, Math.round(size / 128))
  const cx = size / 2
  const cy = size / 2
  const r = size * 0.38

  function strokePath(fn) {
    ctx.beginPath()
    fn()
    ctx.stroke()
  }

  switch (shapeId) {
    case 'heart': {
      const s = size * 0.2
      ctx.beginPath()
      ctx.moveTo(cx, cy + s * 0.3)
      ctx.bezierCurveTo(cx - s, cy - s * 0.8, cx - s * 2.2, cy + s * 0.5, cx, cy + s * 2)
      ctx.bezierCurveTo(cx + s * 2.2, cy + s * 0.5, cx + s, cy - s * 0.8, cx, cy + s * 0.3)
      ctx.stroke()
      break
    }
    case 'star': {
      strokePath(() => {
        const points = 5
        const outer = r
        const inner = r * 0.45
        for (let i = 0; i < points * 2; i++) {
          const rad = (i * Math.PI) / points - Math.PI / 2
          const rr = i % 2 === 0 ? outer : inner
          const x = cx + rr * Math.cos(rad)
          const y = cy + rr * Math.sin(rad)
          if (i === 0) ctx.moveTo(x, y)
          else ctx.lineTo(x, y)
        }
        ctx.closePath()
      })
      break
    }
    case 'flower': {
      const petals = 6
      const petalR = size * 0.12
      const centerR = size * 0.08
      for (let i = 0; i < petals; i++) {
        const a = (i * 2 * Math.PI) / petals - Math.PI / 2
        const px = cx + r * 0.6 * Math.cos(a)
        const py = cy + r * 0.6 * Math.sin(a)
        ctx.beginPath()
        ctx.ellipse(px, py, petalR * 1.5, petalR, a + Math.PI / 2, 0, 2 * Math.PI)
        ctx.stroke()
      }
      ctx.beginPath()
      ctx.arc(cx, cy, centerR, 0, 2 * Math.PI)
      ctx.stroke()
      break
    }
    case 'house': {
      strokePath(() => {
        ctx.moveTo(cx, cy - r)
        ctx.lineTo(cx + r * 0.9, cy + r * 0.4)
        ctx.lineTo(cx + r * 0.9, cy + r)
        ctx.lineTo(cx - r * 0.9, cy + r)
        ctx.lineTo(cx - r * 0.9, cy + r * 0.4)
        ctx.closePath()
      })
      ctx.beginPath()
      ctx.rect(cx - r * 0.35, cy + r * 0.2, r * 0.7, r * 0.8)
      ctx.stroke()
      break
    }
    case 'sun': {
      ctx.beginPath()
      ctx.arc(cx, cy, r * 0.6, 0, 2 * Math.PI)
      ctx.stroke()
      const rays = 12
      const rayLen = size * 0.08
      for (let i = 0; i < rays; i++) {
        const a = (i * 2 * Math.PI) / rays
        ctx.beginPath()
        ctx.moveTo(cx + (r * 0.6) * Math.cos(a), cy + (r * 0.6) * Math.sin(a))
        ctx.lineTo(cx + (r * 0.6 + rayLen) * Math.cos(a), cy + (r * 0.6 + rayLen) * Math.sin(a))
        ctx.stroke()
      }
      break
    }
    case 'fish': {
      strokePath(() => {
        ctx.ellipse(cx, cy, r * 0.9, r * 0.5, 0, 0, 2 * Math.PI)
      })
      ctx.beginPath()
      ctx.moveTo(cx + r * 0.7, cy)
      ctx.lineTo(cx + r * 1.1, cy - r * 0.3)
      ctx.lineTo(cx + r * 1.1, cy + r * 0.3)
      ctx.closePath()
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(cx - r * 0.3, cy - r * 0.15, size * 0.03, 0, 2 * Math.PI)
      ctx.stroke()
      break
    }
    case 'butterfly': {
      const wingR = r * 0.7
      ctx.beginPath()
      ctx.ellipse(cx - wingR * 0.5, cy - wingR * 0.3, wingR * 0.6, wingR, -0.3, 0, 2 * Math.PI)
      ctx.stroke()
      ctx.beginPath()
      ctx.ellipse(cx + wingR * 0.5, cy - wingR * 0.3, wingR * 0.6, wingR, 0.3, 0, 2 * Math.PI)
      ctx.stroke()
      ctx.beginPath()
      ctx.ellipse(cx - wingR * 0.5, cy + wingR * 0.2, wingR * 0.5, wingR * 0.6, -0.2, 0, 2 * Math.PI)
      ctx.stroke()
      ctx.beginPath()
      ctx.ellipse(cx + wingR * 0.5, cy + wingR * 0.2, wingR * 0.5, wingR * 0.6, 0.2, 0, 2 * Math.PI)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(cx - size * 0.02, cy - r)
      ctx.lineTo(cx + size * 0.02, cy + r)
      ctx.stroke()
      break
    }
    default:
      ctx.beginPath()
      ctx.arc(cx, cy, r, 0, 2 * Math.PI)
      ctx.stroke()
  }
  return canvas
}

/** Template IDs for the templates gallery */
export const TEMPLATE_IDS = ['heart', 'star', 'flower', 'house', 'sun', 'fish', 'butterfly']

/** Default cap for uploads / clipboard (keeps edge detection fast on weak devices). */
export const DEFAULT_MAX_IMAGE_LOAD = 2800

/**
 * Higher cap for gallery templates & admin URLs — sharper outlines, less “mushy” detail.
 * Still bounded to avoid mobile OOM (hard cap applied in App when passing options).
 */
export const TEMPLATE_MAX_IMAGE_LOAD = 2800

/**
 * מלבין רק פיקסלים שנראים כמו נייר/רקע (בהירים וכמעט אפורים) — קווי ציור כהים או צבעוניים נשארים כמו במקור.
 * @param {HTMLCanvasElement} canvas
 * @param {object} [options]
 * @param {number} [options.lumMin] — סף בהירות ממוצעת (0–255)
 * @param {number} [options.maxSaturation] — מעל זה נחשב צבע בציור ולא נוגעים (0–1)
 * @param {number} [options.minBrightChannel] — הערוץ הבהיר ביותר חייב להיות לפחות כך
 */
export function whitenColoringTemplatePaperInPlace(canvas, options = {}) {
  if (!canvas?.width || !canvas?.height) return
  const lumMin = typeof options.lumMin === 'number' ? options.lumMin : 224
  const maxSaturation = typeof options.maxSaturation === 'number' ? options.maxSaturation : 0.17
  const minBrightChannel = typeof options.minBrightChannel === 'number' ? options.minBrightChannel : 200

  const w = canvas.width
  const h = canvas.height
  const ctx = canvas.getContext('2d', CTX_READ_OFTEN) || canvas.getContext('2d')
  if (!ctx) return

  const imageData = ctx.getImageData(0, 0, w, h)
  const d = imageData.data
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i]
    const g = d[i + 1]
    const b = d[i + 2]
    const maxc = Math.max(r, g, b)
    const minc = Math.min(r, g, b)
    const lum = 0.299 * r + 0.587 * g + 0.114 * b
    const sat = maxc === 0 ? 0 : (maxc - minc) / maxc
    if (lum >= lumMin && sat <= maxSaturation && maxc >= minBrightChannel) {
      d[i] = 255
      d[i + 1] = 255
      d[i + 2] = 255
    }
  }
  ctx.putImageData(imageData, 0, 0)
}

/**
 * אותיות/צורות שנוצרו בקוד (מילוי שחור על לבן) — צנון canny רגיל מוחק לפעמים את הגופן.
 * shapeAware מתאים יותר לקצוות אנטי-אליאסינג.
 */
export const IMAGE_TO_OUTLINES_LINE_ART = {
  pipeline: 'shapeAware',
  useDeeplabMask: false,
  blurPasses: 2,
  hysteresisHighOffset: 6,
  hysteresisLowRatio: 0.52,
  shapeOffset: 12,
  closePasses: 2,
  erodePasses: 0,
  dilatePasses: 2,
  edgeSmoothPasses: 1,
  edgeSmoothThreshold: 5,
  openPasses: 1,
  finalSmoothPasses: 1,
  finalSmoothThreshold: 6,
}

/** אחוז פיקסלים שחורים (דיו) בדף לאחר עיבוד */
export function measureOutlineInkRatio(outlineCanvas) {
  const w = outlineCanvas.width
  const h = outlineCanvas.height
  if (w < 1 || h < 1) return 0
  const ctx = outlineCanvas.getContext('2d', CTX_READ_OFTEN)
  if (!ctx) return 0
  const d = ctx.getImageData(0, 0, w, h).data
  const n = w * h
  let ink = 0
  for (let i = 0; i < d.length; i += 4) {
    const lum = (d[i] + d[i + 1] + d[i + 2]) / 3
    if (lum < 200) ink++
  }
  return ink / n
}

/**
 * דף צביעה: קווים #000, רקע #fff. אם רוב הדף שחור — הופכים.
 * @returns {boolean} false אם כמעט אין דיו (דף ריק)
 */
export function forceColoringPageBlackOnWhite(outlineCanvas) {
  const w = outlineCanvas.width
  const h = outlineCanvas.height
  const ctx = outlineCanvas.getContext('2d', CTX_READ_OFTEN)
  if (!ctx || w < 2 || h < 2) return false
  const imageData = ctx.getImageData(0, 0, w, h)
  const d = imageData.data
  const n = w * h
  let ink = 0
  for (let i = 0; i < d.length; i += 4) {
    const lum = (d[i] + d[i + 1] + d[i + 2]) / 3
    if (lum < 128) ink++
  }
  const inkRatio = ink / n
  if (inkRatio < 0.0008) {
    ctx.putImageData(imageData, 0, 0)
    return false
  }
  const invert = inkRatio > 0.48
  for (let i = 0; i < d.length; i += 4) {
    let lum = (d[i] + d[i + 1] + d[i + 2]) / 3
    if (invert) lum = 255 - lum
    const v = lum < 128 ? 0 : 255
    d[i] = v
    d[i + 1] = v
    d[i + 2] = v
    d[i + 3] = 255
  }
  ctx.putImageData(imageData, 0, 0)
  return true
}

/** ניגודיות לפני גילוי קצוות — עוזר לצילומים חלשים/אפורים */
function boostGrayContrastInPlace(canvas, { lowPct = 0.02, highPct = 0.98 } = {}) {
  const w = canvas.width
  const h = canvas.height
  const ctx = canvas.getContext('2d', CTX_READ_OFTEN)
  if (!ctx || w < 2 || h < 2) return
  const imageData = ctx.getImageData(0, 0, w, h)
  const d = imageData.data
  const n = w * h
  const hist = new Uint32Array(256)
  const lums = new Uint8ClampedArray(n)
  for (let i = 0; i < n; i++) {
    const o = i * 4
    const L = Math.round((d[o] * 299 + d[o + 1] * 587 + d[o + 2] * 114) / 1000)
    lums[i] = L
    hist[L]++
  }
  const pct = (p) => {
    const target = Math.max(1, Math.min(n, Math.floor(p * n)))
    let acc = 0
    for (let g = 0; g < 256; g++) {
      acc += hist[g]
      if (acc >= target) return g
    }
    return 255
  }
  const lo = pct(lowPct)
  const hi = pct(highPct)
  const span = Math.max(8, hi - lo)
  for (let i = 0; i < n; i++) {
    const L = Math.max(0, Math.min(255, Math.round(((lums[i] - lo) * 255) / span)))
    const o = i * 4
    d[o] = L
    d[o + 1] = L
    d[o + 2] = L
  }
  ctx.putImageData(imageData, 0, 0)
}

/** פריסט צילום: גבולות דקים — strokeBand מוקדם מונע גושים שחורים; סגירה/חיבור מינימליים */
export const CAMERA_PHOTO_OUTLINE_OPTS = {
  pipeline: 'shapeAware',
  useDeeplabMask: false,
  blurPasses: 1,
  shapeOffset: 11,
  strokeBandEarlyPasses: 1,
  closePasses: 0,
  erodePasses: 0,
  dilatePasses: 0,
  edgeSmoothPasses: 0,
  edgeSmoothThreshold: 5,
  openPasses: 0,
  thinPasses: 4,
  postThinDilatePasses: 0,
  postThinMajorityPasses: 0,
  finalClosePasses: 0,
  connectEndpoints: false,
  endpointMaxDist: 12,
  endpointMaxConnections: 120,
  endpointSkeletonIters: 2,
  postConnectThinIters: 0,
  minComponentSize: 1,
  finalSmoothPasses: 0,
  finalSmoothThreshold: 5,
  pruneSpursIters: 1,
}

const CAMERA_OUTLINE_FALLBACK_CANNY = {
  pipeline: 'canny',
  useDeeplabMask: false,
  blurPasses: 1,
  hysteresisHighOffset: 3,
  hysteresisLowRatio: 0.48,
  closePasses: 0,
  erodePasses: 0,
  dilatePasses: 0,
  edgeSmoothPasses: 0,
  edgeSmoothThreshold: 5,
  openPasses: 0,
  thinPasses: 2,
  connectEndpoints: false,
  endpointMaxDist: 12,
  endpointMaxConnections: 120,
  minComponentSize: 1,
  finalClosePasses: 0,
  finalSmoothPasses: 0,
  finalSmoothThreshold: 5,
  pruneSpursIters: 1,
}

const CAMERA_OUTLINE_FALLBACK_XDOG = {
  pipeline: 'xdog',
  useDeeplabMask: false,
  blurPasses: 2,
  xdogSmallPasses: 1,
  xdogLargePasses: 5,
  xdogK: 1.4,
  xdogContrast: 7.0,
  xdogTau: 0.72,
  xdogEps: 0.001,
  closePasses: 0,
  erodePasses: 0,
  dilatePasses: 0,
  edgeSmoothPasses: 0,
  edgeSmoothThreshold: 5,
  openPasses: 0,
  thinPasses: 3,
  finalClosePasses: 0,
  connectEndpoints: false,
  endpointMaxDist: 12,
  endpointMaxConnections: 120,
  minComponentSize: 1,
  finalSmoothPasses: 0,
  finalSmoothThreshold: 5,
  pruneSpursIters: 1,
}

const CAMERA_OUTLINE_PRESETS = [
  CAMERA_PHOTO_OUTLINE_OPTS,
  CAMERA_OUTLINE_FALLBACK_CANNY,
  CAMERA_OUTLINE_FALLBACK_XDOG,
]

/**
 * צילום → דף צביעה שחור-לבן: מסלול ייעודי עם ניסיונות חלופיים אם אין מספיק קווים.
 * @param {HTMLCanvasElement} sourceCanvas — תמונה אחרי loadImageToCanvas
 * @returns {{ canvas: HTMLCanvasElement, inkRatio: number }}
 */
export function cameraPhotoToOutlineCanvas(sourceCanvas) {
  const work = document.createElement('canvas')
  work.width = sourceCanvas.width
  work.height = sourceCanvas.height
  const wctx = work.getContext('2d', CTX_READ_OFTEN)
  if (!wctx) throw new Error('cameraPhotoToOutlineCanvas: no 2d context')
  wctx.drawImage(sourceCanvas, 0, 0)
  try {
    prepCameraFrameForOutlineExtraction(work)
  } catch {
    /* ignore */
  }
  boostGrayContrastInPlace(work)

  let bestCanvas = null
  let bestInk = 0
  let bestScore = -1

  for (const opts of CAMERA_OUTLINE_PRESETS) {
    let candidate
    try {
      candidate = imageToOutlines(work, opts)
    } catch {
      continue
    }
    forceColoringPageBlackOnWhite(candidate)
    const ink = measureOutlineInkRatio(candidate)
    const inRange = ink >= 0.003 && ink <= 0.58
    const score = inRange ? ink : ink * 0.35
    if (score > bestScore) {
      bestScore = score
      bestInk = ink
      bestCanvas = candidate
    }
  }

  if (!bestCanvas) {
    bestCanvas = imageToOutlines(work, CAMERA_PHOTO_OUTLINE_OPTS)
    forceColoringPageBlackOnWhite(bestCanvas)
    bestInk = measureOutlineInkRatio(bestCanvas)
  }

  try {
    finalizePrintableColoringOutline(bestCanvas, {
      gapClosePasses: 0,
      lineThickenPasses: 0,
      balanceErodePasses: 0,
    })
  } catch {
    /* ignore */
  }
  forceColoringPageBlackOnWhite(bestCanvas)
  normalizeOutlineCanvasPolarity(bestCanvas)
  forceColoringPageBlackOnWhite(bestCanvas)
  bestInk = measureOutlineInkRatio(bestCanvas)

  return { canvas: bestCanvas, inkRatio: bestInk }
}

/**
 * טרום־צילום לפני גילוי קצוות.
 * חשוב: לא משתמשים בהלבנת "נייר" רחבה — עיפרון/עפרון אפור (lum≈205) נכלל בשגיאה
 * ונעלם, ואז נשארים קווים "לבנים" על overlay.
 * רק בההבה מהודק למשטח כמעט לבן; שאר הפיקסלים נטפל בהם בשלב ההשחזה אחרי edge.
 */
export function prepCameraFrameForOutlineExtraction(canvas) {
  if (!canvas?.width || !canvas?.height) return
  try {
    whitenColoringTemplatePaperInPlace(canvas, {
      lumMin: 251,
      maxSaturation: 0.058,
      minBrightChannel: 243,
    })
  } catch {
    /* ignore */
  }
}

/**
 * WebView / מצלמה באנדרואיד לעיתים מחזירים type ריק או octet-stream. מאפשרים ניסיון טעינה כתמונה.
 * @param {Blob|File} fileOrBlob
 */
export function isLikelyImageFile(fileOrBlob) {
  if (!fileOrBlob) return false
  const mime = String(fileOrBlob.type || '').toLowerCase().trim()
  if (mime.startsWith('image/')) return true
  if (mime === '' || mime === 'application/octet-stream') return true
  return false
}

/**
 * שלילת צבע (בהיר↔כהה). מעדיף ctx.filter (פחות getImageData — יציב יותר ב-WebView).
 * @param {HTMLCanvasElement} sourceCanvas
 * @returns {HTMLCanvasElement}
 */
export function invertRgbCanvas(sourceCanvas) {
  const w = sourceCanvas.width
  const h = sourceCanvas.height
  const out = document.createElement('canvas')
  out.width = w
  out.height = h
  const outCtx = out.getContext('2d') || out.getContext('2d', CTX_READ_OFTEN)
  if (!outCtx) throw new Error('Canvas 2d context not available (invertRgbCanvas output)')

  try {
    if ('filter' in outCtx) {
      outCtx.filter = 'invert(100%)'
      outCtx.drawImage(sourceCanvas, 0, 0, w, h)
      outCtx.filter = 'none'
      return out
    }
  } catch {
    /* fall through */
  }

  const sctx = sourceCanvas.getContext('2d', CTX_READ_OFTEN) || sourceCanvas.getContext('2d')
  if (!sctx) throw new Error('Canvas 2d context not available (invertRgbCanvas)')
  const imageData = sctx.getImageData(0, 0, w, h)
  const d = imageData.data
  for (let i = 0; i < d.length; i += 4) {
    d[i] = 255 - d[i]
    d[i + 1] = 255 - d[i + 1]
    d[i + 2] = 255 - d[i + 2]
  }
  outCtx.putImageData(imageData, 0, 0)
  return out
}

/**
 * Load image from file or URL to canvas (max dimension for performance).
 * Uses high-quality smoothing when scaling down so photos stay cleaner before outline extraction.
 *
 * Android WebView: never set crossOrigin on blob:/object URLs — it often breaks camera/gallery loads.
 * Fallbacks: createImageBitmap (some codecs), then FileReader data URL.
 */
export async function loadImageToCanvas(fileOrUrl, maxSize = DEFAULT_MAX_IMAGE_LOAD) {
  const scaleAndDraw = (source) => {
    let { width, height } = source;
    if (width > maxSize || height > maxSize) {
      if (width > height) {
        height = (height / width) * maxSize;
        width = maxSize;
      } else {
        width = (width / height) * maxSize;
        height = maxSize;
      }
    }
    width = Math.max(1, Math.round(width));
    height = Math.max(1, Math.round(height));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', CTX_READ_OFTEN) || canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2d context not available (loadImageToCanvas)');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, width, height);
    if (typeof source.close === 'function') source.close();
    return canvas;
  };

  const decodeFromSrc = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      if (typeof src === 'string' && /^https?:\/\//i.test(src)) {
        img.crossOrigin = 'anonymous';
      }
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = src;
    });

  if (typeof fileOrUrl === 'string') {
    const img = await decodeFromSrc(fileOrUrl);
    return scaleAndDraw(img);
  }

  const blob = fileOrUrl;
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await decodeFromSrc(objectUrl);
    URL.revokeObjectURL(objectUrl);
    return scaleAndDraw(img);
  } catch {
    URL.revokeObjectURL(objectUrl);
    if (typeof createImageBitmap === 'function') {
      try {
        const bmp = await createImageBitmap(blob);
        return scaleAndDraw(bmp);
      } catch {
        /* continue */
      }
    }
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => reject(new Error('FileReader failed'));
      fr.readAsDataURL(blob);
    });
    const img = await decodeFromSrc(dataUrl);
    return scaleAndDraw(img);
  }
}
